import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

const MODULE_META = [
  { id: 1, icon: "ri-layout-4-line",       count: 24, image: "https://public.readdy.ai/ai/img_res/7b84f960c9be0efc26196bce5b56f16f.jpg" },
  { id: 2, icon: "ri-menu-line",            count: 18, image: "https://readdy.ai/api/search-image?query=navigation%20menu%20design%20components%2C%20top%20navigation%20bar%20with%20logo%20and%20links%2C%20mobile%20hamburger%20menu%20expanded%2C%20clean%20dark%20UI%20navigation%20system%2C%20minimal%20design%20approach&width=480&height=200&seq=mod002&orientation=landscape" },
  { id: 3, icon: "ri-shopping-bag-line",    count: 30, image: "https://readdy.ai/api/search-image?query=premium%20luxury%20e-commerce%20boutique%20website%20interface%20displaying%20elegant%20product%20cards%20with%20high%20quality%20lifestyle%20photography%2C%20gold%20price%20tags%20and%20stylish%20add%20to%20cart%20buttons%2C%20sophisticated%20minimal%20online%20fashion%20store%20design%2C%20warm%20ivory%20and%20cream%20background%2C%20refined%20product%20grid%20with%20generous%20whitespace%2C%20upscale%20retail%20brand%20aesthetic%2C%20professional%20desktop%20UI%20screenshot&width=480&height=200&seq=mod-ecom-new01&orientation=landscape" },
  { id: 4, icon: "ri-file-list-3-line",     count: 22, image: "https://readdy.ai/api/search-image?query=form%20UI%20components%20design%2C%20clean%20input%20fields%20text%20areas%20dropdowns%20checkboxes%2C%20modern%20form%20design%20system%2C%20dark%20themed%20form%20interface%20with%20golden%20accents%2C%20professional%20web%20forms&width=480&height=200&seq=mod004&orientation=landscape" },
  { id: 5, icon: "ri-gallery-line",         count: 20, image: "https://readdy.ai/api/search-image?query=media%20gallery%20components%20design%2C%20image%20grid%20masonry%20layout%2C%20video%20player%20interface%2C%20carousel%20slider%20UI%2C%20photography%20portfolio%20display%20design%2C%20dark%20minimal%20aesthetic&width=480&height=200&seq=mod005&orientation=landscape" },
  { id: 6, icon: "ri-bar-chart-box-line",   count: 16, image: "https://readdy.ai/api/search-image?query=data%20visualization%20dashboard%20UI%20components%2C%20bar%20charts%20line%20graphs%20progress%20indicators%20KPI%20cards%2C%20dark%20mode%20analytics%20interface%2C%20professional%20business%20intelligence%20design&width=480&height=200&seq=mod006&orientation=landscape" },
];

export default function ModuleLibrarySection() {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation(0.08);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="modules" className="py-28 bg-[#0C0C0C]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div ref={ref} className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 mb-14">
          <div>
            <span className={`text-[#C9A96E] text-sm font-semibold tracking-widest uppercase ${isVisible ? "anim-fade-up" : "opacity-0"}`}>
              {t("mod.eyebrow")}
            </span>
            <h2 className={`text-4xl xl:text-5xl font-bold text-white mt-3 leading-tight ${isVisible ? "anim-fade-up anim-d-100" : "opacity-0"}`}>
              {t("mod.title")}
            </h2>
            <p className={`text-white/40 text-base mt-3 max-w-xl leading-relaxed ${isVisible ? "anim-fade-up anim-d-200" : "opacity-0"}`}>
              {t("mod.sub")}
            </p>
          </div>
          <div className={`flex items-center gap-2 text-white/30 text-sm ${isVisible ? "anim-fade-up anim-d-300" : "opacity-0"}`}>
            <i className="ri-cursor-line" />
            <span>{t("mod.hint")}</span>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {MODULE_META.map((mod, i) => {
            const cKey = `c${i}`;
            return (
              <div
                key={mod.id}
                onMouseEnter={() => setHovered(mod.id)}
                onMouseLeave={() => setHovered(null)}
                className={`module-expand-card bg-[#161616] border rounded-2xl overflow-hidden cursor-pointer transition-colors duration-300 ${
                  hovered === mod.id ? "border-[#C8953A]/40" : "border-white/8"
                } ${isVisible ? `anim-scale-in anim-d-${(i + 1) * 100}` : "opacity-0"}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-colors duration-300 ${hovered === mod.id ? "bg-[#C8953A]" : "bg-white/5"}`}>
                      <i className={`${mod.icon} text-xl transition-colors duration-300 ${hovered === mod.id ? "text-[#0C0C0C]" : "text-[#C8953A]"}`} />
                    </div>
                    <div className="text-right">
                      <div className="text-[#C8953A] text-2xl font-bold">{mod.count}</div>
                      <div className="text-white/25 text-xs">{t(`mod.${cKey}.unit`)}</div>
                    </div>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{t(`mod.${cKey}.name`)}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{t(`mod.${cKey}.desc`)}</p>
                </div>

                <div className="px-6 pb-6 opacity-0 transition-opacity duration-300" style={{ opacity: hovered === mod.id ? 1 : 0 }}>
                  <div className="w-full h-28 rounded-xl overflow-hidden mb-4">
                    <img src={mod.image} alt={t(`mod.${cKey}.name`)} className="w-full h-full object-top object-cover" />
                  </div>
                  <div className="space-y-2 mb-4">
                    {[0, 1, 2, 3].map((j) => (
                      <div key={j} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C8953A] flex-shrink-0" />
                        <span className="text-white/60 text-xs">{t(`mod.${cKey}.i${j}`)}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => scrollTo("contact")}
                    className="whitespace-nowrap w-full text-center text-xs font-semibold text-[#C8953A] border border-[#C8953A]/30 hover:bg-[#C8953A]/10 rounded-xl py-2.5 transition-colors duration-200 cursor-pointer"
                  >
                    {t("mod.apply")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 bg-[#161616] border border-white/8 rounded-3xl p-10 flex flex-col xl:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-white text-2xl font-bold mb-2">{t("mod.cta.title")}</h3>
            <p className="text-white/40 text-sm">{t("mod.cta.sub")}</p>
          </div>
          <button
            onClick={() => scrollTo("contact")}
            className="whitespace-nowrap bg-[#C8953A] hover:bg-[#a97b30] text-[#0C0C0C] font-semibold px-8 py-4 rounded-full text-sm transition-all duration-200 cursor-pointer flex-shrink-0"
          >
            {t("mod.cta.btn")}
            <i className="ri-arrow-right-line ml-2" />
          </button>
        </div>
      </div>
    </section>
  );
}
